import { useIMask } from 'react-imask';
import { useIsFirstRender } from 'usehooks-ts';
import { useEffect, useMemo, useState } from 'react';

import TextInput, { InputProps } from 'components/text-input';

export type MaskOpts = Parameters<typeof useIMask>[0];

export type MaskedNumberInputProps = Omit<_MaskedNumberInputProps, 'format' | 'decimal' | 'sign'>;

type _MaskedNumberInputProps = {
  value?: number;
  onChange: (value?: string) => void;
  sign?: string;
  min?: number;
  max?: number;
  decimal?: boolean;
  format?: (value: number) => string;
  maskOpts?: MaskOpts;
} & Omit<InputProps, 'onChange' | 'value'>;

const MaskedNumberInput: React.FC<_MaskedNumberInputProps> = (props) => {
  const { onChange, sign, placeholder, format, value, min, max, decimal, maskOpts, ...rest } =
    props;

  const opts = useMemo(
    () =>
      maskOpts ??
      ({
        mask: [
          { mask: '' },
          {
            mask: `num ${sign}`,
            lazy: false,
            expose: true,
            blocks: {
              num: {
                min,
                max,
                mask: Number,
                expose: true,
                thousandsSeparator: ' ',
                overwrite: true,
                ...(decimal
                  ? {
                      scale: 2,
                      radix: ',',
                      mapToRadix: ['.'],
                      padFractionalZeros: true,
                      normalizeZeros: true,
                    }
                  : { scale: 0 }),
              },
            },
          },
        ],
      } as MaskOpts),
    [sign, min, max, decimal, maskOpts],
  );

  const isFirstRender = useIsFirstRender();
  const defaultValue = useState(() => (value ? format?.(value) : ''))[0];

  const { ref, maskRef } = useIMask(opts, {
    onComplete(_, maskRef) {
      if (!isFirstRender) {
        onChange(maskRef.unmaskedValue === '' ? undefined : maskRef.unmaskedValue);
      }
    },
  });

  useEffect(() => {
    const strVal = (value ?? '') + '';
    if (maskRef.current && maskRef.current.unmaskedValue !== strVal) {
      maskRef.current.unmaskedValue = strVal;
    }
  }, [value, maskRef]);

  return (
    <TextInput
      ref={ref}
      defaultValue={defaultValue}
      inputMode='numeric'
      placeholder={placeholder || `0 ${sign}`}
      onChange={(val) => {
        // Очистка clearButton
        if (val === '') maskRef.current!.value = '';
      }}
      {...rest}
    />
  );
};

export default MaskedNumberInput;
